// utils/paymentHandler.ts
import { lighten } from '@mui/material';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { roundToNext30Min } from '../../services/IsClosed';

interface PaymentHandlerParams {
  // State values
  state: {
    undeliverablePostalCodes: any;
    undeliverableDistances: any;
    undeliverableTravelTime: any;
    orderType: 'Delivery' | 'Pickup' | 'Event';
    savedAddresses: any;
    selectedAddressId: any;
    firstName: string | undefined;
    lastName: string | undefined;
    email: string | undefined;
    address1: string | undefined;
    postalCode: string | undefined;
    city: string | undefined;
    stateProvince: string | undefined;
    notes: string | undefined;
    distanceInMiles: number;
    travelTimeInMins: number;
    laterSchedDate: Date;
    eventDate: Date;
    eventStart: Date;
    eventFieldStartTime: Date;
    requestedPickupAndDeliveryTime: Date;
    calculatedEstimatedTimeFromAPI: Date;
    total: number;
    orderPickListOptions: any[];
    openHoursList: any;
  };
  
  // Props/dependencies
  organizationSettings: any;
  organization: any;
  appUserDetails: any;
  order: any;
  
  // Callback functions
  validateGuestCount: () => boolean;
  validateMenuCategoryItems: (payload: any) => Promise<any>;
  validateOrder: () => Promise<{ status: number }>;
  postCreatePaymentLink: (payload: any, processorType: number) => Promise<any>;
  orderRemoveItem: (itemId: number) => Promise<void>;
  fetchCalculatedEstimatedTime: (payload: any) => Promise<any>; 
  getOrganizationOrdersOpenSchedule: (orderType: string) => Promise<any>;
  getCalculatedEstimatedTimeFromAPI: () => void;
  handleClickOpenPayMethodModel: () => void;
  
  // State setters
  setState: (updates: any) => void;
  
  // Other dependencies
  onPaymentLinkCreated: (paymentNumber: string, processorType: number, amountCents: number, applySurcharge: boolean) => void;
}

export const handlePayNow = async (
  event: any,
  params: PaymentHandlerParams
) => {
  const {
    state,
    organizationSettings,
    organization,
    appUserDetails,
    order,
    validateGuestCount,
    validateMenuCategoryItems,
    validateOrder,
    postCreatePaymentLink,
    orderRemoveItem,
    getCalculatedEstimatedTimeFromAPI,
    handleClickOpenPayMethodModel,
    fetchCalculatedEstimatedTime,
    getOrganizationOrdersOpenSchedule,
    setState,
    onPaymentLinkCreated
  } = params;

  // Validate guest count
  if (!validateGuestCount()) {
    return;
  }

  setState({ paymentDeclineError: null });
  event.preventDefault();

  const {
    undeliverablePostalCodes,
    undeliverableDistances,
    undeliverableTravelTime,
    orderType,
    savedAddresses,
    selectedAddressId,
    firstName,
    lastName,
    email,
    address1,
    postalCode,
    city,
    stateProvince,
    notes,
    distanceInMiles,
    travelTimeInMins,
    laterSchedDate,
    eventDate,
    eventFieldStartTime,
    requestedPickupAndDeliveryTime,
    calculatedEstimatedTimeFromAPI,
    total,
    orderPickListOptions,
    openHoursList,
  } = state;

  // Build event start time
  const start = moment(eventDate).format('YYYY-MM-DD') + 
                moment(eventFieldStartTime).utc().format('THH:mm:ss.SSS[Z]');

  // Check delivery availability
  const selectedAddress = savedAddresses?.find((address: any) => address?.id === selectedAddressId);
  const invalidPostalCode = selectedAddress 
    ? undeliverablePostalCodes?.find((code: string) => code === selectedAddress?.postalCode)
    : undeliverablePostalCodes?.find((code: string) => code === postalCode);
  const invalidDistanceInMiles = undeliverableDistances?.find((dist: string) => distanceInMiles >= Number(dist));
  const invalidDriveDuration = undeliverableTravelTime?.find((time: string) => travelTimeInMins >= Number(time));

  if (orderType === "Delivery" && (invalidPostalCode || invalidDistanceInMiles || invalidDriveDuration)) {
    setState({ 
      orderCreateError: true, 
      orderCreateErrorMessage: organizationSettings?.ordersUndeliverableMessage 
    });
    return;
  }

  // Validate order date/time
  const orderDateTime = orderType === "Event" 
    ? moment(start)
    : moment(laterSchedDate).set({
        hour: moment(requestedPickupAndDeliveryTime).hour(),
        minute: moment(requestedPickupAndDeliveryTime).minute(),
        second: moment(requestedPickupAndDeliveryTime).second()
      });

  if (orderDateTime.isBefore(moment())) {
    setState({ 
      orderCreateError: true, 
      orderCreateErrorMessage: "Please select a Date and Time in the future"
    });
    return;
  }

  const openHoursResponse = await getOrganizationOrdersOpenSchedule(orderType);
  const scheduleType = openHoursResponse?.data ? orderType : "Base";

  const responseDate = await fetchCalculatedEstimatedTime({ orderItems: order.orderItems, scheduleType }); 

  const responseMoment = moment.utc(responseDate.data).local();
  const orderMomentUTC = moment(orderDateTime);

  if (orderMomentUTC.isBefore(responseMoment)) {
    const roundedTime = roundToNext30Min(responseMoment.toDate());
    const roundedDate = roundedTime instanceof Date ? roundedTime : (roundedTime as any).toDate();
    const isValidDate = roundedDate instanceof Date && !isNaN(roundedDate.getTime());

    const originalDate = moment(laterSchedDate).format("YYYY-MM-DD");
    const newDate = moment(roundedDate).format("YYYY-MM-DD");
    const dateChanged = originalDate !== newDate;

    setState({ 
      laterSchedDate: isValidDate ? new Date(roundedDate) : new Date(),
      requestedPickupAndDeliveryTime: isValidDate ? new Date(roundedDate) : new Date(),
      openHoursList: {
        ...openHoursList,
        openFrom: new Date(roundedDate)
      },
      eventDate: isValidDate ? new Date(roundedDate) : new Date(),
      eventFieldStartTime: isValidDate ? new Date(roundedDate) : new Date(),
      dateUpdatedMessage: dateChanged ? `Date was updated, please review` : "",
      timeUpdatedMessage: `Time was updated, please review`,
      eventDateUpdatedMessage: dateChanged ? `Date was updated, please review` : "" ,
      eventTimeUpdatedMessage: `Time was updated, please review`,
      orderCreateError: true, 
      orderCreateErrorMessage: `The earliest available date and time is ${moment(roundedDate).format("YYYY/MM/DD hh:mm A")}`,
    });
    return;
  }

  const menuCategoryItems = order.orderItems.map((item: any) => item?.menuCategoryItem?.id);
  const response = await validateMenuCategoryItems({ 
    "organizationId": organization?.id, 
    "menuCategoryItems": menuCategoryItems
  });

  if (response?.data?.menuCategoryItems && response?.data?.menuCategoryItems.length > 0) {
    const invalidItemsIds = response?.data?.menuCategoryItems;
    
    for (const invalidItemId of invalidItemsIds) {
      const invalidItem = order.orderItems.find(
        (item: any) => item?.menuCategoryItem?.id === invalidItemId
      );
      
      if (invalidItem) {
        const itemName = invalidItem?.name || 
                        invalidItem.menuCategoryItem?.menuItem?.name || 
                        'Unknown Item';
        
        setState({ 
          orderCreateError: true, 
          orderCreateErrorMessage: `Item ${itemName} is no longer available`
        });
        
        await orderRemoveItem(invalidItem.id);
      }
    }
    
    getCalculatedEstimatedTimeFromAPI();
    return;
  }

  // Determine processor type
  let processorType = 0;
  if (organizationSettings?.merchantAccount?.includes('t1_') || 
      organizationSettings?.merchantAccount?.includes('p1_')) {
    processorType = 5;
  }
  if (organizationSettings?.merchantAccount?.includes('acct_')) {
    processorType = 3;
  }

  setState({ loadPaymentUrl: true });

  try {
    const validationResponse = await validateOrder();
    if (validationResponse.status !== 200) {
      throw new Error('Order validation failed.');
    }
  } catch (error) {
    console.error('Unable to validate order before payment:', error);
    setState({
      loadPaymentUrl: false,
      orderCreateError: true,
      orderCreateErrorMessage: 'There has been an error validating your Order, please try again.',
    });
    return;
  }

  // Build payment payload
  const payload = {
    "amountToCharge": Math.round(total * 100),
    "availablePaymentTypes": "cc",
    "ccEmail": "sonny.elhamahmy@gmail.com",
    "backgroundColor": "#8CB4D2",
    "paymentNumber": uuidv4(),
    "payor_address": {
      "line1": address1 ? address1 : "123 South Main",
      "city": city ? city : "Naples",
      "country": "US",
      "postal_code": postalCode ? postalCode : "34102",
      "region": stateProvince ? stateProvince : "F1"
    },
    "payerEmail": appUserDetails?.emailAddress ? appUserDetails.emailAddress : email,
    "payer": `${firstName} ${lastName}`,
    "dueDate": moment().add(1, 'd').toDate(),
    "encodedLogo": "",
    "encodedLogoPosition": "left",
    "expirationDate": moment().add(2, 'd').toDate(),
    "invoiceIntroductionMessage": "Thank you for choosing HORIZON for your recent order. Please click the <br />\"Authorize Now\" button below to authorize and store your credit card for future payments on your order.",
    "invoiceMessage": "If you have any question regarding this credit card authorization, please call our office. Thanks again for doing business with HORIZON.<br /><br />Regards,<br /><br />Your HORIZON Team",
    "invoiceNumber": uuidv4(),
    "accountId": organizationSettings?.merchantAccount ? organizationSettings.merchantAccount : "1b6de8fb-203f-4984-9bdc-059f9c11e10c",
    "merchantName": organization?.name,
    "paymentLinkType": 0,
    "zipCode": selectedAddress?.postalCode,
    "productName": "Caterease Online Order",
    "replyEmail": organizationSettings?.contactEmailAddress,
    "sendEmail": true,
    "senderName": organization?.name,
    "showCardOnFileDialog": true,
    "showTip": organizationSettings?.enableAddTip,
    "textColor": "#000000",
    "transaction_details": {
      "itemized_receipt": [],
      "note": notes
    },
    "serialNumber": organizationSettings?.serialNumber,
    "applySurcharge": organizationSettings?.applySurcharge,
    "surchargePercent": organizationSettings?.surchargePercentage * 100,
    "showSuggestedTip": organizationSettings?.enableAddTip,
    "tipPct1": organizationSettings?.tipPercent1 * 10000,
    "tipPct2": organizationSettings?.tipPercent2 * 10000,
    "tipPct3": organizationSettings?.tipPercent3 * 10000,
    "tipBackgroundColor": organizationSettings?.layoutColor && lighten(organizationSettings?.layoutColor, 0.5),
    "submitButtonBackgroundColor": organizationSettings?.layoutColor,
    "orderPickListOptions": orderPickListOptions || [],
  };

  // Create payment link
  try {
    const response = await postCreatePaymentLink(payload, processorType);
    if (response?.iframeUrl && response?.paymentNumber) {
      const paymentUrl = new URL(response.iframeUrl);
      paymentUrl.searchParams.set('viewType', 'graze');
      const iFrameUrl = paymentUrl.toString();
      onPaymentLinkCreated(response.paymentNumber, processorType, payload.amountToCharge, !!payload.applySurcharge);
      setState({ paymentURL: iFrameUrl, loadPaymentUrl: false });
      handleClickOpenPayMethodModel();
    } else {
      throw new Error('The payment link response is missing its URL or payment number.');
    }
  } catch (error) {
    console.error('Unable to create payment link:', error);
    setState({
      loadPaymentUrl: false,
      orderCreateError: true,
      orderCreateErrorMessage: 'Unable to open the payment form. Please try again.',
    });
  }
};